// Data formats shared by the pipeline, the API and the web app. No imports, so all of them can use this file.

/** Original value of one editable element, used to keep the snapshot HTML until it is edited. */
export interface Orig {
  /** text: element text; link: href (+ text when the link text is editable); image: src + alt. */
  k: 'text' | 'link' | 'image'
  text?: string
  href?: string
  src?: string
  alt?: string
}

/** One section design: a top-level section of a migrated page, as HTML with edit markers. */
export interface SectionDesign {
  /** "<layoutKey>.<n>", the value of the `design` field of `section` blocks. */
  key: string
  /** Human name shown in the editor, from the section's first heading. */
  name: string
  html: string
  /** The section's editable rows with their original values, as the page extraction lists them. */
  rows?: ItemRow[]
}

/**
 * Page layout. `before` + sections + `after` is the
 * snapshot page. Editable elements carry `data-hh-edit="<id>"`; the element whose content is
 * replaced on a text edit carries `data-hh-t="<id>"`.
 */
export interface Layout {
  key: string
  kind: 'page' | 'post'
  /** Site path the layout was captured from ("/about/"). */
  path: string
  title: string
  description: string
  before: string
  after: string
  sections: SectionDesign[]
  /** Original values of every editable element in this layout, by edit id. */
  fields: Record<string, Orig>
  /** Templates of the menu copies marked data-hh-menu="primary:<index>". */
  menus: MenuTemplate[]
  /** Head facts of the original page. */
  seo?: PageSeo
}

/**
 * Head facts of one original page, captured at crawl and kept as data so the renderer writes the
 * head tags itself. Title and description are entry fields (editable); these are the rest.
 */
export interface PageSeo {
  /** og:type of the original ("website", "article"). */
  type?: string
  /** Social image: the original og:image, else the page's first large image. A site path or an absolute URL. */
  image?: string | null
  imageAlt?: string | null
  /** The original page asked search engines not to index it. */
  noindex?: boolean
  /** Other-language versions (link rel=alternate hreflang), URLs as found. */
  hreflang?: Array<{ lang: string; href: string }>
  /** Entry fields the exporter generated because the original had none. */
  generated?: Array<'title' | 'description'>
  /** Posts: article dates and author from the original (ISO dates). */
  published?: string | null
  modified?: string | null
  author?: string | null
}

/** Site-wide SEO facts found on the original site. Nothing here is invented: absent means not found. */
export interface SiteSeo {
  /** Site name: og:site_name, the Organization name, else the home title's first part. */
  name: string
  /** html lang of the home page ("en-GB"). */
  lang?: string | null
  /** og:locale ("en_GB"). */
  locale?: string | null
  /** Default social image (site path or absolute URL). */
  image?: string | null
  /** Short summary of the site: the home page description. */
  description?: string | null
  /** The business behind the site, from the original structured data and the page chrome. */
  org?: OrgFacts | null
  /** Origins of the original site (http and https, with and without www): URLs on them are rewritten to the live origin. */
  origins?: string[]
}

export interface OrgFacts {
  /** schema.org type: "Organization", "LocalBusiness" or a LocalBusiness subtype ("Bakery"). */
  type: string
  name: string
  logo?: string | null
  telephone?: string | null
  email?: string | null
  /** schema.org PostalAddress properties as found. */
  address?: Record<string, string> | null
  /** schema.org openingHoursSpecification objects, or openingHours strings, as found. */
  openingHours?: unknown[] | null
  sameAs?: string[] | null
}

/** Markup pattern of one copy of the primary menu, used to rebuild it when the menu is edited. */
export interface MenuTemplate {
  li: string
  a: string
  parentLi: string
  subUl: string
  subLi: string
  subA: string
  /** Original <li> markup by item signature, reused when an item is unchanged. */
  original: Record<string, string>
}

/** Menu item. The editor also keeps
 * the stored item's type and reference, so a page link stays a page link when the menu is saved. */
export interface MenuNode {
  label: string
  /** Resolved link; for `custom` items, the stored address. */
  url: string
  target?: string | null
  /** Item type: custom, page, post, taxonomy or collection. Absent means custom. */
  type?: string
  /** The entry or term a non-custom item points at. */
  ref?: { collection: string; id: string } | null
  titleAttr?: string | null
  cssClasses?: string | null
  children?: MenuNode[]
}

/** Item signature: label, URL and children. Must match the exporter's. */
export function menuSig(items: MenuNode[]): string {
  return JSON.stringify(items.map((i) => [i.label, i.url, menuSig(i.children ?? [])]))
}

/** Feature ids; must match FEATURE_IDS in packages/shared/src/features.ts (a pipeline test checks it). */
export const SITE_FEATURES = ['contact', 'blog', 'gallery', 'llm'] as const
export type SiteFeature = (typeof SITE_FEATURES)[number]

/**
 * AI crawler policy in robots.txt (llm feature): every crawler allowed; AI search and assistants
 * allowed but model training refused; every AI crawler refused. Must match AI_CRAWLER_PRESETS in
 * packages/shared/src/features.ts.
 */
/**
 * A boolean entry field. A stored column can be 1/0 (SQLite)
 * and as a boolean from others. Null when the field is unset (sites exported before it).
 */
export const flag = (v: unknown): boolean | null => (v === true || v === 1 ? true : v === false || v === 0 ? false : null)

export const AI_CRAWLER_PRESETS = ['allow', 'search', 'block'] as const
export type AiCrawlers = (typeof AI_CRAWLER_PRESETS)[number]

export interface Redirect {
  from: string
  to: string
}

/** A row of the `items` repeater (section blocks and header/footer parts). */
export interface ItemRow {
  ref?: string | null
  kind?: string | null
  label?: string | null
  text?: string | null
  url?: string | null
  alt?: string | null
  image?: MediaValue | null
}

export interface MediaValue {
  id?: string
  src?: string
  alt?: string
  provider?: string
  meta?: Record<string, unknown>
}

/** Value of one block in a page's `sections` field. */
export interface SectionBlock {
  _type: string
  _key?: string
  design?: string | null
  name?: string | null
  items?: ItemRow[] | null
  /** rich_text blocks */
  body?: unknown[] | null
  /** gallery blocks */
  images?: GalleryImage[] | null
}

/** A row of a gallery block's `images` repeater. */
export interface GalleryImage {
  image?: MediaValue | null
  alt?: string | null
  caption?: string | null
}

/** Link targets a customer may set: web, email, phone and SMS links, site paths, anchors and queries.
 * Anything else (javascript:, data:, relative "page.html") is refused when saved and ignored when rendered.
 * The Hedgehog app imports this file for the same check (apps/web/src/components/app/editor/link-field.tsx). */
export const SAFE_URL = /^(https?:|mailto:|tel:|sms:|\/|#|\?)/i

/** What SAFE_URL accepts, in words, for error messages. */
export const LINK_RULE = 'Links must start with https://, http://, mailto:, tel:, sms:, / (a page of this site) or # (a place on the page)'

/** Most blocks a page's sections field holds (the `maxItems` of the field in seed/seed.json). */
export const MAX_SECTIONS = 60

/** Content slug of a site path: "/" is "index", "/a/b/" is "a--b". The Hedgehog editor imports it. */
export function slugForPath(path: string): string {
  let p = path
  try {
    p = decodeURIComponent(path)
  } catch {
    /* keep the raw path */
  }
  const s = p.replace(/^\/+|\/+$/g, '').replace(/\/+/g, '--').toLowerCase()
  return s || 'index'
}

/** Site path of a content slug; the inverse of slugForPath. */
export function pathForSlug(slug: string): string {
  return slug === 'index' ? '/' : `/${slug.replace(/--/g, '/')}/`
}
